<template>
  <AppModal
    :visible="isOpen === true"
    :title="`Edit Step Details (${editableData.nodeType !== undefined && editableData.nodeType !== null ? editableData.nodeType : 'Step'})`"
    :box-class="modalBoxClass"
    :render-mode="renderMode"
    :panel-plain="renderMode === 'panel'"
    @close="closeModal"
  >
    <div class="step-editor-shell">
      <div class="step-editor-tabbar" role="tablist" aria-label="Step editor tabs">
        <button type="button" class="step-editor-tab" :class="{ 'step-editor-tab-active': activeTab === 'general' }" @click="activeTab = 'general'">General</button>
        <button v-if="showConditionTab === true" type="button" class="step-editor-tab" :class="{ 'step-editor-tab-active': activeTab === 'condition' }" @click="activeTab = 'condition'">Condition</button>
        <button v-if="showToolsTab === true" type="button" class="step-editor-tab" :class="{ 'step-editor-tab-active': activeTab === 'tools' }" @click="activeTab = 'tools'">Tools</button>
        <button v-if="showButtonsTab === true" type="button" class="step-editor-tab" :class="{ 'step-editor-tab-active': activeTab === 'buttons' }" @click="activeTab = 'buttons'">Buttons</button>
      </div>

      <div v-show="activeTab === 'general'" class="step-editor-tab-panel">
        <div class="form-control mb-4">
          <label class="label">
            <span class="label-text">Step Name</span>
          </label>
          <input
            type="text"
            v-model="editableData.label"
            class="input input-bordered w-full"
            placeholder="Enter step name"
            required
          />
        </div>

        <div class="form-control mb-4" v-if="showQuestionFields === true">
          <label class="label">
            <span class="label-text">Message to say to the user</span>
          </label>
          <textarea
            v-model="editableData.message"
            class="textarea textarea-bordered w-full"
            rows="3"
            placeholder="Enter the message the agent should say at this step"
          ></textarea>
        </div>

        <div class="form-control flex flex-row mb-4 items-start" v-if="showQuestionFields === true">
          <label class="label cursor-pointer py-0">
            <input
              type="checkbox"
              v-model="editableData.useExactMessage"
              class="checkbox checkbox-primary mr-4"
            />
            <span class="label-text">Use exact message</span>
          </label>
          <Tooltip position="right">
            <template #default>
              <p class="text-xs text-content2">If checked, the AI will use this exact message phrasing and will not attempt to rephrase it based on the conversation context.</p>
            </template>
          </Tooltip>
        </div>

        <div class="form-control mb-4">
          <label class="label">
            <span class="label-text">Describe the step</span>
          </label>
          <textarea
            v-model="editableData.stepDescription"
            class="textarea textarea-bordered w-full"
            rows="3"
            placeholder="Describe the purpose or details of this step"
          ></textarea>
        </div>

        <div class="mb-4" v-if="showQuestionFields === true || editableData.nodeType === 'KnowledgeBase'">
          <label class="label">
            <span class="label-text">Data to Collect (The required data from the user to continue)</span>
          </label>
          <div v-for="(variable, index) in editableData.variablesToCollect" :key="index" class="p-3 bg-base-200 rounded-lg mb-3">
            <div class="flex items-center mb-2">
              <input
                type="text"
                v-model="variable.name"
                class="input input-bordered input-sm flex-grow mr-2"
                placeholder="Variable name"
              />
              <button type="button" @click="removeVariable(index)" class="btn btn-sm btn-circle btn-ghost">✕</button>
            </div>
            <div class="step-editor-variable-row mb-2">
              <select v-model="variable.type" class="select select-bordered select-sm w-full">
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="boolean">Boolean</option>
                <option value="date">Date</option>
              </select>
              <input
                type="text"
                v-model="variable.description"
                class="input input-bordered input-sm w-full"
                placeholder="Description"
              />
            </div>
            <div class="form-control">
              <label class="label cursor-pointer justify-start">
                <input type="checkbox" v-model="variable.required" class="checkbox checkbox-xs mr-4" />
                <span class="label-text">Required field</span>
              </label>
            </div>
            <div class="step-editor-variable-row mb-2">
              <select v-model="variable.validationPattern" class="select select-bordered select-sm w-full">
                <option v-for="option in regexValidationOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
              <input
                v-if="variable.validationPattern === 'custom'"
                type="text"
                v-model="variable.customRegex"
                class="input input-bordered input-sm w-full"
                placeholder="Custom regex, for example ^[A-Z]{2}\\d{6}$"
              />
            </div>
          </div>
          <button type="button" @click="addVariable" class="btn btn-sm w-full mt-2">+ Add Data</button>
        </div>
      </div>

      <div v-show="activeTab === 'condition' && showConditionTab === true" class="step-editor-tab-panel">
        <div class="form-control mb-4">
          <label class="label">
            <span class="label-text">Variable Path</span>
          </label>
          <input
            type="text"
            v-model="editableData.config.sourceKey"
            class="input input-bordered w-full"
            placeholder="customerType or lastActionResult.output.status"
          />
          <div class="text-xs text-gray-500 mt-2">Use any collected variable or nested runtime value.</div>
        </div>
        <div class="step-editor-condition-row mb-4">
          <div class="form-control">
            <label class="label">
              <span class="label-text">Operator</span>
            </label>
            <select v-model="editableData.config.operator" class="select select-bordered w-full">
              <option value="equals">Equals</option>
              <option value="notEquals">Does Not Equal</option>
              <option value="contains">Contains</option>
              <option value="notContains">Does Not Contain</option>
              <option value="greaterThan">Greater Than</option>
              <option value="greaterThanOrEqual">Greater Than Or Equal</option>
              <option value="lessThan">Less Than</option>
              <option value="lessThanOrEqual">Less Than Or Equal</option>
              <option value="exists">Exists</option>
              <option value="notExists">Does Not Exist</option>
              <option value="isTrue">Is True</option>
              <option value="isFalse">Is False</option>
              <option value="isEmpty">Is Empty</option>
              <option value="isNotEmpty">Is Not Empty</option>
            </select>
          </div>
          <div class="form-control">
            <label class="label">
              <span class="label-text">Compare Value</span>
            </label>
            <input
              type="text"
              v-model="editableData.config.right"
              class="input input-bordered w-full"
              placeholder="vip, true, 100, {{otherVariable}}"
            />
          </div>
        </div>
        <div class="form-control mb-4">
          <label class="label">
            <span class="label-text">Notes</span>
          </label>
          <textarea
            v-model="editableData.stepDescription"
            class="textarea textarea-bordered w-full"
            rows="3"
            placeholder="Describe when this branch should be true."
          ></textarea>
        </div>
        <div class="rounded-lg bg-base-200 p-3 text-xs text-gray-600">
          Connect this node to other nodes, then label outgoing connections as true or false. If labels are empty and there are two outgoing connections, the first connection is treated as true and the second as false.
        </div>
      </div>

      <div v-show="activeTab === 'tools' && showToolsTab === true" class="step-editor-tab-panel">
        <div class="form-control mb-6">
          <label class="label">
            <span class="label-text">Tools to Use (for LLM context/augmentation)</span>
          </label>
          <div class="step-editor-badge-row" v-if="editableData.toolsToUse !== null && editableData.toolsToUse !== undefined && editableData.toolsToUse.length > 0">
            <span v-for="toolId in editableData.toolsToUse" :key="toolId" class="badge badge-info step-editor-tool-badge">
              {{ getToolName(toolId) }}
              <button type="button" @click="toggleToolSelection(toolId)" class="btn btn-xs btn-circle btn-ghost">✕</button>
            </span>
          </div>
          <div class="dropdown dropdown-end w-full">
            <button type="button" tabindex="0" class="btn w-full justify-start font-normal">
              {{ toolSelectionLabel }}
              <svg class="w-4 h-4 ml-auto fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
            </button>
            <ul tabindex="0" class="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-full max-h-60 overflow-y-auto">
              <li v-for="tool in availableTools" :key="tool.toolid">
                <label class="label cursor-pointer">
                  <span class="label-text">{{ tool.toolname }}</span>
                  <input
                    type="checkbox"
                    :checked="isToolSelected(tool.toolid) === true"
                    @change="toggleToolSelection(tool.toolid)"
                    class="checkbox checkbox-primary"
                  />
                </label>
              </li>
              <li v-if="availableTools.length === 0">
                <span class="label-text p-2 text-gray-500">No tools available</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div v-show="activeTab === 'buttons' && showButtonsTab === true" class="step-editor-tab-panel">
        <div class="step-editor-button-toolbar mb-4">
          <button type="button" @click="addStepButton" class="btn btn-sm">Add Button +</button>
        </div>
        <div class="overflow-x-auto">
          <table class="table w-full">
            <thead>
              <tr>
                <th>Label</th>
                <th>Type</th>
                <th>Action / Target</th>
                <th class="w-1/5">Controls</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="editableData.buttons === undefined || editableData.buttons === null || editableData.buttons.length === 0">
                <td colspan="4" class="text-center text-gray-500">No buttons configured for this step.</td>
              </tr>
              <tr v-for="button in editableData.buttons" :key="button.id">
                <td>{{ button.label }}</td>
                <td>{{ button.type }}</td>
                <td class="truncate max-w-xs" :title="getButtonActionLabel(button)">
                  {{ getButtonActionLabel(button) }}
                </td>
                <td>
                  <button @click="editStepButton(button)" class="btn btn-xs btn-info">Edit</button>
                  <button @click="deleteStepButton(button.id)" class="btn btn-xs btn-error step-editor-inline-button">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <template #actions>
      <button type="button" class="btn" @click="closeModal">Cancel</button>
      <button type="button" class="btn" @click="saveChanges">Save</button>
    </template>
  </AppModal>

  <StepButtonEditorModal
    :is-open="isButtonEditorModalOpen === true"
    :button-data="currentEditingButton"
    :available-steps="availableJumpTargets"
    @close="isButtonEditorModalOpen = false"
    @save="handleSaveStepButton"
  />
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import StepButtonEditorModal from './StepButtonEditorModal.vue';
import Tooltip from './Tooltip.vue';
import AppModal from './AppModal.vue';

const props = defineProps({
  isOpen: { 
    type: Boolean, 
    required: true 
  },
  nodeData: { 
    type: Object, 
    default: () => ({}) 
  },
  availableTools: {
    type: Array,
    default: () => []
  },
  allFlowNodes: {
    type: Array,
    default: () => []
  },
  renderMode: {
    type: String,
    default: 'modal'
  }
});

const emit = defineEmits(['close', 'save']);

const editableData = ref({ config: {}, variablesToCollect: [], toolsToUse: [], buttons: [], useExactMessage: false });
const activeTab = ref('general');
const isButtonEditorModalOpen = ref(false);
const currentEditingButton = ref(null);
const regexValidationOptions = [
  { value: '', label: 'No regex validation' },
  { value: 'digits', label: 'Digits only' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone number' },
  { value: 'israeliId', label: 'Israeli ID' },
  { value: 'dateIso', label: 'Date YYYY-MM-DD' },
  { value: 'time24h', label: 'Time HH:mm' },
  { value: 'uuid', label: 'UUID' },
  { value: 'custom', label: 'Custom regex' }
];

const showQuestionFields = computed(() => {
  return editableData.value !== null && editableData.value !== undefined && 
         (editableData.value.nodeType === 'Question' || editableData.value.nodeType === 'Root');
});

const showConditionTab = computed(() => {
  return editableData.value !== null && editableData.value !== undefined && editableData.value.nodeType === 'If';
});

const showToolsTab = computed(() => {
 return editableData.value !== null && editableData.value !== undefined &&
        (editableData.value.nodeType === 'Question' || editableData.value.nodeType === 'Root');
});

const showButtonsTab = computed(() => {
  return editableData.value !== null && editableData.value !== undefined &&
         (editableData.value.nodeType === 'Question' || editableData.value.nodeType === 'Root' || editableData.value.nodeType === 'Action');
});

const modalBoxClass = computed(() => {
  if (props.renderMode === 'panel') {
    return 'step-editor-panel-shell';
  }
  return 'w-11/12 max-w-3xl step-editor-modal-box';
});

const toolSelectionLabel = computed(() => {
  if (editableData.value.toolsToUse === null || editableData.value.toolsToUse === undefined || editableData.value.toolsToUse.length === 0) {
    return 'Select Tools';
  }
  return `${editableData.value.toolsToUse.length} Tools Selected`;
});

const availableJumpTargets = computed(() => {
  if (props.allFlowNodes === undefined || props.allFlowNodes === null || editableData.value === undefined || editableData.value === null || editableData.value.id === undefined) {
    return [];
  }
  return props.allFlowNodes
    .filter(node => node.id !== editableData.value.id) 
    .map(node => ({ 
      value: node.id,
      label: node.data !== null && node.data !== undefined ? node.data.label : node.id 
    }));
});

watch(() => props.nodeData, (newData) => {
  if (newData !== null && newData !== undefined) {
    editableData.value = normalizeEditableData(JSON.parse(JSON.stringify(newData))); 
  } else {
     editableData.value = normalizeEditableData({});
  }
  activeTab.value = 'general';
}, { immediate: true, deep: true });

function normalizeEditableData(data) {
  if (data.config === null || data.config === undefined || typeof data.config !== 'object') {
    data.config = {};
  }
  if (data.variablesToCollect === null || data.variablesToCollect === undefined) {
    data.variablesToCollect = [];
  } else {
    data.variablesToCollect = data.variablesToCollect.map(v => {
      if (typeof v === 'string') {
        return { name: v, type: 'string', description: '', validationPattern: '', customRegex: '' };
      }
      if (v !== null && v !== undefined && typeof v === 'object') {
        if (typeof v.validationPattern !== 'string') {
          v.validationPattern = '';
        }
        if (typeof v.customRegex !== 'string') {
          v.customRegex = '';
        }
      }
      return v;
    });
  }
  if (data.toolsToUse === null || data.toolsToUse === undefined) {
    data.toolsToUse = [];
  }
  if (data.buttons === null || data.buttons === undefined) {
    data.buttons = [];
  }
  if (data.useExactMessage === null || data.useExactMessage === undefined) {
    data.useExactMessage = false; 
  }
  if (typeof data.config.sourceKey !== 'string') {
    data.config.sourceKey = '';
  }
  if (typeof data.config.sourceType !== 'string') {
    data.config.sourceType = 'variable';
  }
  if (typeof data.config.operator !== 'string' || data.config.operator.length === 0) {
    data.config.operator = 'equals';
  }
  if (typeof data.config.left !== 'string') {
    data.config.left = '';
  }
  if (typeof data.config.right !== 'string') {
    data.config.right = '';
  }
  return data;
}

function addVariable() {
  if (editableData.value.variablesToCollect === null || editableData.value.variablesToCollect === undefined) {
      editableData.value.variablesToCollect = [];
  }
  editableData.value.variablesToCollect.push({ name: '', type: 'string', description: '', validationPattern: '', customRegex: '' });
}

function removeVariable(index) {
  editableData.value.variablesToCollect.splice(index, 1);
}

function getToolName(toolId) {
  const tool = props.availableTools.find(t => t.toolid === toolId);
  return tool !== null && tool !== undefined ? tool.toolname : 'Unknown Tool';
}

function isToolSelected(toolId) {
  if (editableData.value.toolsToUse === null || editableData.value.toolsToUse === undefined) {
    return false;
  }
  return editableData.value.toolsToUse.includes(toolId);
}

function toggleToolSelection(toolId) {
   if (editableData.value.toolsToUse === null || editableData.value.toolsToUse === undefined) {
      editableData.value.toolsToUse = [];
  }
  const index = editableData.value.toolsToUse.indexOf(toolId);
  if (index === -1) {
    editableData.value.toolsToUse.push(toolId);
  } else {
    editableData.value.toolsToUse.splice(index, 1);
  }
}

function getJumpTargetLabel(stepId) {
  for (let i = 0; i < availableJumpTargets.value.length; i = i + 1) {
    const target = availableJumpTargets.value[i];
    if (target !== null && target !== undefined && target.value === stepId) {
      if (typeof target.label === 'string' && target.label.length > 0) {
        return target.label;
      }
      return stepId;
    }
  }
  return stepId;
}

function getButtonActionLabel(button) {
  if (button === null || button === undefined) {
    return '';
  }
  if (button.type === 'JumpToStep') {
    return getJumpTargetLabel(button.action);
  }
  if (typeof button.action === 'string') {
    return button.action;
  }
  if (button.action === undefined || button.action === null) {
    return '';
  }
  return String(button.action);
}

function addStepButton() {
  currentEditingButton.value = null;
  isButtonEditorModalOpen.value = true;
}

function editStepButton(button) {
  currentEditingButton.value = JSON.parse(JSON.stringify(button));
  isButtonEditorModalOpen.value = true;
}

function deleteStepButton(buttonId) {
  if (editableData.value.buttons !== null && editableData.value.buttons !== undefined) {
    editableData.value.buttons = editableData.value.buttons.filter(b => b.id !== buttonId);
  }
}

function handleSaveStepButton(buttonData) {
  if (editableData.value.buttons === null || editableData.value.buttons === undefined) {
    editableData.value.buttons = [];
  }
  const index = editableData.value.buttons.findIndex(b => b.id === buttonData.id);
  if (index > -1) {
    editableData.value.buttons.splice(index, 1, buttonData);
  } else {
    editableData.value.buttons.push(buttonData);
  }
  isButtonEditorModalOpen.value = false;
}

function closeModal() {
  activeTab.value = 'general';
  emit('close');
}

function saveChanges() {
  if (editableData.value.variablesToCollect !== undefined && editableData.value.variablesToCollect !== null) {
    editableData.value.variablesToCollect = editableData.value.variablesToCollect.filter(v => (v.name !== undefined && v.name !== null) && v.name.trim().length > 0);
    for (let index = 0; index < editableData.value.variablesToCollect.length; index = index + 1) {
      const variable = editableData.value.variablesToCollect[index];
      if (variable.validationPattern !== 'custom') {
        variable.customRegex = '';
      }
    }
  }
  emit('save', editableData.value);
  if (props.renderMode !== 'panel') {
    closeModal();
  }
}
</script>

<style scoped>
.step-editor-modal-box {
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.step-editor-panel-shell,
.step-editor-shell {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.step-editor-tabbar {
  display: flex;
  align-items: center;
  padding: 0.15rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.35rem;
  background: #f8fafc;
}

.step-editor-tab + .step-editor-tab {
  margin-left: 0.5rem;
}

.step-editor-tab {
  border: none;
  border-radius: 0.25rem;
  padding: 0.4rem 0.6rem;
  background: transparent;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 600;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.step-editor-tab-active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: inset 0 0 0 1px #e2e8f0;
}

.step-editor-tab-panel {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 0.45rem;
  padding: 0.55rem 0.6rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.35rem;
  background: #ffffff;
}

.step-editor-variable-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.step-editor-condition-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.step-editor-variable-row > * + * {
  margin-left: 0.5rem;
}

.step-editor-condition-row > * + * {
  margin-left: 0.5rem;
}

.step-editor-badge-row {
  margin-bottom: 0.5rem;
}

.step-editor-tool-badge {
  margin-bottom: 0.5rem;
}

.step-editor-tool-badge + .step-editor-tool-badge {
  margin-left: 0.5rem;
}

.step-editor-button-toolbar {
  display: flex;
  justify-content: flex-end;
}

.step-editor-inline-button {
  margin-left: 0.25rem;
}

.step-editor-panel-shell :deep(.label-text) {
  font-size: 0.72rem;
  font-weight: 600;
  color: #334155;
}

.step-editor-panel-shell :deep(.label) {
  padding-top: 0;
  padding-bottom: 0.2rem;
  min-height: 0;
}

.step-editor-panel-shell :deep(.input),
.step-editor-panel-shell :deep(.textarea),
.step-editor-panel-shell :deep(.select) {
  font-size: 0.78rem;
}

.step-editor-panel-shell :deep(.textarea) {
  min-height: 3.5rem;
}

.step-editor-panel-shell :deep(.form-control.mb-4) {
  margin-bottom: 0.55rem;
}
</style> 